import type { EventId } from '@ps/web-sports-api-types';
import { isNil } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { Participants } from '@swf/sports/types/event';
import type { EventTypeId } from '@swf/sports/types/eventType';
import type { Orientation } from '@swf/sports/types/scoreboards';
import { isFootballScoreboard, isTennisScoreboard } from '@swf/sports/utils/isScoreboard';

import FootballScoreboard from './components/FootballScoreboard/FootballScoreboard';
import TennisScoreboard from './components/TennisScoreboard/TennisScoreboard';
import Unavailable from './components/Unavailable/Unavailable';

export interface Props {
  eventId: EventId;
  eventTypeId: EventTypeId;
  isGoingInPlay: boolean;
  orientation?: Orientation;
  participants: Participants | undefined;
}

const Scoreboard: React.FC<Props> = ({
  eventId,
  eventTypeId,
  isGoingInPlay,
  orientation = 'vertical',
  participants
}) => {
  const eventScoreboard = useSelector((rootState: RootState) =>
    select.scoreboards.eventScoreboard(rootState, eventId)
  );

  if (!participants) {
    return null;
  }

  if (isGoingInPlay) {
    return (
      <Unavailable
        participants={participants}
        eventTypeId={eventTypeId}
        orientation={orientation}
      />
    );
  }

  if (eventScoreboard?.hasError) {
    return (
      <Unavailable
        participants={participants}
        eventTypeId={eventTypeId}
        orientation={orientation}
      />
    );
  }

  if (isNil(eventScoreboard?.data)) {
    return (
      <Unavailable
        participants={participants}
        eventTypeId={eventTypeId}
        isLoading
        orientation={orientation}
      />
    );
  }

  if (isFootballScoreboard(eventScoreboard)) {
    return (
      <FootballScoreboard
        participants={participants}
        footballFixture={eventScoreboard.data}
        orientation={orientation}
      />
    );
  }

  if (isTennisScoreboard(eventScoreboard)) {
    return <TennisScoreboard tennisMatch={eventScoreboard.data} participants={participants} />;
  }

  return null;
};

export default Scoreboard;
